<template>
    <div class="RiskMannAsistenteIA">
        <!-- Botón flotante (patrón FlechaSubir): logo gato con ojos que
             siguen el cursor y lean magnético. Al pasar el cursor muestra una
             sugerencia según la vista actual y el gato mira hacia la burbuja.
             Reacciona al scroll y salta al pulsar la flecha de volver arriba. -->
        <a v-if="!abierto" id="btn-asistente-ia" role="button" tabindex="0"
            aria-label="Asistente Riskmann IA" data-abre-asistente
            :class="{ 'con-boton-arriba': conBotonArriba }"
            @mouseenter="alEntrarFab" @mouseleave="alSalirFab" @click="abrirPanel"
            @keydown.enter.prevent="abrirPanel" @keydown.space.prevent="abrirPanel">
            <LogoGato :estado="estadoBot" interactivo magnetico reactivo-scroll
                :saltos="saltosGato" :objetivo="objetivoFab" />
        </a>

        <transition name="burbuja-aparece" mode="out-in">
            <div v-if="mensajeFab && !abierto" :key="mensajeFab" ref="burbujaFab"
                class="burbuja-fab" :class="{ 'con-boton-arriba': conBotonArriba }" aria-hidden="true">
                {{ mensajeFab }}
            </div>
        </transition>

        <!-- Panel flotante en la esquina del botón. Sin fondo oscuro: la vista
             queda visible para que el usuario VEA la navegación asistida
             mientras conversa. Con v-show el chat sigue montado al cerrarlo,
             así no se pierde una respuesta en curso ni la narración. -->
        <transition name="panel-aparece">
            <div v-show="abierto" id="panelAsistenteIA" ref="panelFlotante" class="panel-asistente"
                role="dialog" aria-labelledby="tituloAsistenteIA" tabindex="-1">
                <div class="encabezado-asistente d-flex align-items-start text-white">
                    <div class="d-flex align-items-center gap-3 flex-grow-1 me-2 min-w-0">
                        <div class="avatar-asistente flex-shrink-0" :class="'estado-' + estadoBot">
                            <LogoGato :estado="estadoBot" interactivo />
                        </div>
                        <div class="min-w-0">
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                                <h5 id="tituloAsistenteIA" class="titulo-asistente mb-0">
                                    Riskmann IA
                                </h5>
                                <span class="estado-enlinea">
                                    <span class="punto-enlinea" :class="estadoBot"></span>{{ textoEstado }}
                                </span>
                            </div>
                            <div class="subtitulo mt-1">Tu asistente inteligente para Seguridad Vial</div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <div class="dropdown">
                            <button type="button" class="btn btn-encabezado" data-bs-toggle="dropdown"
                                aria-expanded="false" title="Opciones">
                                <i class="fas fa-ellipsis-v"></i>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                <li>
                                    <button type="button" class="dropdown-item" @click="borrarConversacion">
                                        <i class="fas fa-trash-alt me-2 text-muted"></i>Borrar conversación
                                    </button>
                                </li>
                                <li v-if="esAdmin">
                                    <button type="button" class="dropdown-item" @click="guardarEntrenamiento">
                                        <i class="fas fa-graduation-cap me-2 text-muted"></i>Guardar para entrenamiento
                                    </button>
                                </li>
                            </ul>
                        </div>
                        <button type="button" class="btn btn-encabezado" aria-label="Cerrar" title="Cerrar"
                            @click="cerrarPanel(true)">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
                <div class="cuerpo-asistente">
                    <PanelChat ref="panel" @estado="estadoBot = $event" @admin="esAdmin = $event" />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import LogoGato from "@/components/AsistenteIA/LogoGato.vue";

// Tope del backend por mensaje (AGENT_MAX_INPUT_CHARS), el mismo que usa App.vue
const MAX_PREGUNTA = 500;

// Sugerencias de la burbuja del FAB según la vista actual. Se busca el PRIMER
// prefijo que coincida con $route.name, así que lo específico va antes de lo
// general ("SeguridadVialInspecciones" antes que "SeguridadVial").
const MENSAJES_FAB = {
    SeguridadVialMisInspecciones: [
        "¿Te ayudo con tus inspecciones? Pregúntame cómo crear una.",
        "Puedo explicarte el paso a paso de la inspección preoperacional.",
    ],
    SeguridadVialInspecciones: [
        "¿Te ayudo con las inspecciones? Pregúntame cómo crear una.",
        "Puedo explicarte el paso a paso de la inspección preoperacional.",
    ],
    SeguridadVialSiniestros: [
        "¿Dudas registrando un siniestro? Te guío paso a paso.",
        "Pregúntame por el reporte y seguimiento de siniestros.",
    ],
    SeguridadVialVehiculos: [
        "Te explico cómo gestionar tu flota de vehículos.",
        "¿Necesitas crear o editar un vehículo? Pregúntame cómo.",
    ],
    SeguridadVialConductores: [
        "Te ayudo con la gestión de tus conductores.",
        "Pregúntame cómo vincular o editar un conductor.",
    ],
    SeguridadVialMantenimientos: [
        "¿Te explico cómo registrar un mantenimiento?",
        "Pregúntame por el historial de mantenimientos de tu flota.",
    ],
    SeguridadVialPlanesDesplazamientos: [
        "Pregúntame por los planes de desplazamiento.",
        "¿Te ayudo a crear un plan de desplazamiento?",
    ],
    SeguridadVialAptitudesFisicas: [
        "Te guío con las aptitudes físicas de tus conductores.",
        "¿Dudas con una evaluación de aptitud física? Pregúntame.",
    ],
    SeguridadVialFormatos: [
        "Te explico cómo trabajar con los formatos.",
        "¿Necesitas ayuda con un formato? Pregúntame.",
    ],
    SeguridadVialCuestionarios: [
        "Te explico cómo funcionan los cuestionarios.",
        "¿Te ayudo a configurar un cuestionario?",
    ],
    SeguridadVialNovedades: [
        "¿Te ayudo a registrar una justificación?",
        "Pregúntame cómo consultar las justificaciones de tu equipo.",
    ],
    Novedades: [
        "¿Te ayudo con el registro de novedades?",
        "Pregúntame cómo consultar las novedades de tu equipo.",
    ],
    SeguridadVialInfografias: [
        "¿Te ayudo con las infografías? Te explico cómo crearlas.",
        "Pregúntame por las estadísticas de visualización de tus infografías.",
    ],
    SeguridadVial: [
        "¿Te ayudo con la gestión de seguridad vial?",
        "Puedo guiarte o llevarte a la vista que necesites.",
    ],
    // Empresas / espacios de trabajo (específicos antes que "Empresas")
    EmpresasVinculaciones: [
        "¿Te ayudo con las vinculaciones de tu espacio de trabajo?",
        "Pregúntame cómo vincular un trabajador paso a paso.",
    ],
    EmpresasInvitacionesVinculacion: [
        "¿Te explico cómo invitar trabajadores a tu espacio?",
        "Pregúntame por los enlaces de invitación de vinculación.",
    ],
    EmpresasDesvinculaciones: [
        "¿Dudas con las desvinculaciones? Te explico el proceso.",
    ],
    EmpresasMisEmpresas: [
        "¿Te ayudo a crear o gestionar tus espacios de trabajo?",
    ],
    EmpresasProfesiogramas: [
        "¿Te explico cómo trabajar con los profesiogramas?",
        "Pregúntame por cargos y exámenes de los profesiogramas.",
    ],
    EmpresasHorario: [
        "¿Te ayudo con los horarios de tu empresa?",
    ],
    EmpresasMisRegionales: [
        "¿Dudas con las regionales? Pregúntame.",
    ],
    EmpresasGestionDocumental: [
        "¿Te explico la gestión documental de tu empresa?",
    ],
    EmpresaDatosGenerales: [
        "¿Te ayudo con los datos generales de tu empresa?",
    ],
    Empresas: [
        "¿Te ayudo con tu espacio de trabajo? Pregúntame.",
    ],
    SolicitudesVinculacion: [
        "¿Te explico cómo solicitar la vinculación a una empresa?",
    ],
    // Otros módulos
    Estadisticas: [
        "¿Te explico las estadísticas que estás viendo?",
    ],
    Evento: [
        "¿Te ayudo con los eventos? Pregúntame cómo funcionan.",
    ],
    Notificaciones: [
        "¿Dudas con tus notificaciones? Pregúntame.",
    ],
    MonitoreoGeografico: [
        "¿Te explico el monitoreo geográfico de tu flota?",
    ],
    ControlPersonal: [
        "¿Te ayudo con el control de personal?",
    ],
    ControlDiario: [
        "¿Te ayudo con el control diario de personal?",
    ],
    AusentismoLaboral: [
        "¿Dudas con el ausentismo laboral? Pregúntame.",
    ],
    PanelAdministrativo: [
        "¿Te ayudo con el panel administrativo?",
    ],
    PlanesAccion: [
        "¿Te explico cómo gestionar tus planes de acción?",
        "Pregúntame por las actividades de mejora y su seguimiento.",
    ],
    Reportes: [
        "¿Te ayudo con los reportes? Pregúntame.",
    ],
    Rutogramas: [
        "¿Dudas con los rutogramas? Te explico.",
    ],
    SoporteTecnico: [
        "¿Necesitas ayuda? También puedes preguntarme a mí primero.",
    ],
    Capacitacion: [
        "¿Dudas con las capacitaciones? Se gestionan en el Campus; te explico.",
        "Pregúntame por evaluaciones, resultados o certificados del Campus.",
    ],
    UsuarioMiPerfil: [
        "¿Te ayudo con tu perfil o tus solicitudes de vinculación?",
    ],
    Inicio: [
        "¡Hola! Soy Riskmann IA. Pregúntame cómo usar la plataforma.",
        "Puedo guiarte paso a paso o llevarte a donde necesites.",
    ],
};

const MENSAJES_FAB_DEFECTO = [
    "¡Hola! Soy Riskmann IA. Pregúntame cómo usar la plataforma.",
    "Puedo guiarte paso a paso o llevarte a la vista que necesites.",
];

// Cada cuánto rota la sugerencia mientras el cursor siga sobre el FAB
const INTERVALO_ROTACION_MS = 3500;

export default {
    name: "RiskMannAsistenteIA",

    components: {
        LogoGato,
        PanelChat: () => import("@/components/AsistenteIA/PanelChat.vue"),
    },

    props: {
        // La flecha de volver arriba (FlechaSubir.vue) está visible debajo:
        // el botón sube para dejarle espacio
        conBotonArriba: { type: Boolean, default: false },
        // Clics en la flecha de volver arriba: cada uno hace saltar al gato
        saltosGato: { type: Number, default: 0 },
    },

    data() {
        return {
            abierto: false,
            // reposo | pensando | narrando — lo emite PanelChat
            estadoBot: "reposo",
            // Sugerencia visible en la burbuja del FAB ("" = oculta)
            mensajeFab: "",
            // Punto {x, y} de la burbuja hacia donde mira el gato del FAB
            objetivoFab: null,
            // Admin SOFU (lo emite PanelChat tras consultar /api/agent/sesion)
            esAdmin: false,
        };
    },

    computed: {
        textoEstado() {
            if (this.estadoBot === "pensando") return "Pensando…";
            if (this.estadoBot === "narrando") return "Narrando…";
            return "En línea";
        },

        preguntaDerivada() {
            return this.$store.state.asistenteIA.preguntaPendiente;
        },
    },

    watch: {
        // App.vue captura ?asistente= en su mounted, que corre DESPUÉS del
        // mounted de este hijo (Vue monta hijos antes que el padre): un watcher
        // con immediate cubre ambos órdenes sin depender del timing.
        preguntaDerivada: {
            immediate: true,
            handler(pregunta) {
                if (!pregunta) return;
                // NO se consume aquí: una instancia transitoria (la ruta inicial
                // aún sin resolver) podría limpiar la pregunta y morir sin
                // entregarla. Se consume al ENTREGAR, en esperarYEnviar.
                this.$nextTick(() => this.abrirConPregunta(pregunta));
            },
        },

        // App.vue oculta la flecha de volver arriba mientras el panel ocupa la esquina
        abierto(valor) {
            this.$emit("cambio-panel", valor);
        },
    },

    mounted() {
        // Sin fondo oscuro, el clic fuera del panel o Escape deben cerrarlo manualmente
        document.addEventListener("click", this.cerrarSiClicFuera);
        document.addEventListener("keydown", this.cerrarConEscape);
        // Otras vistas lo abren con this.$root.$emit("abrir-asistente-ia", { pregunta })
        // (ej. la presentación del asistente en el Inicio)
        this.$root.$on("abrir-asistente-ia", this.abrirDesdeEvento);
    },

    beforeDestroy() {
        document.removeEventListener("click", this.cerrarSiClicFuera);
        document.removeEventListener("keydown", this.cerrarConEscape);
        this.$root.$off("abrir-asistente-ia", this.abrirDesdeEvento);
        if (this._timerRotacion) clearInterval(this._timerRotacion);
        // La ruta siguiente puede no montar el asistente: liberar la esquina
        if (this.abierto) this.$emit("cambio-panel", false);
        this._destruido = true;
    },

    methods: {
        // ── Apertura y cierre del panel ──────────────────────────────────
        abrirPanel() {
            this.alSalirFab();
            this.abierto = true;
            this.$nextTick(() => {
                const panel = this.$refs.panelFlotante;
                if (panel) panel.focus({ preventScroll: true });
            });
        },

        cerrarPanel(devolverFoco) {
            this.abierto = false;
            if (!devolverFoco) return;
            // Cerrado con teclado o con la ✕: el foco vuelve al botón flotante
            this.$nextTick(() => {
                const boton = document.getElementById("btn-asistente-ia");
                if (boton) boton.focus();
            });
        },

        abrirDesdeEvento(datos) {
            const pregunta = String((datos && datos.pregunta) || "").trim().slice(0, MAX_PREGUNTA);
            if (pregunta) this.abrirConPregunta(pregunta);
            else this.abrirPanel();
        },

        cerrarSiClicFuera(evento) {
            if (!this.abierto) return;
            const objetivo = evento.target;
            const panel = this.$refs.panelFlotante;
            if (panel && panel.contains(objetivo)) return;
            // Lo que abre el asistente (su botón, la presentación del Inicio)
            // no debe cerrarlo con el mismo clic que lo abrió
            if (objetivo && objetivo.closest && objetivo.closest("[data-abre-asistente]")) return;
            this.cerrarPanel(false);
        },

        cerrarConEscape(evento) {
            if (evento.key === "Escape" && this.abierto) this.cerrarPanel(true);
        },

        // ── Burbuja de sugerencias del FAB ───────────────────────────────
        alEntrarFab() {
            if (this._timerRotacion) clearInterval(this._timerRotacion);
            this._mensajesVista = this.mensajesParaVista();
            this._indiceMensaje = 0;
            this.mensajeFab = this._mensajesVista[0];
            this.apuntarMiradaABurbuja();
            // Rota la sugerencia mientras el cursor siga encima
            this._timerRotacion = setInterval(() => {
                this._indiceMensaje = (this._indiceMensaje + 1) % this._mensajesVista.length;
                this.mensajeFab = this._mensajesVista[this._indiceMensaje];
                this.apuntarMiradaABurbuja();
            }, INTERVALO_ROTACION_MS);
        },

        alSalirFab() {
            if (this._timerRotacion) clearInterval(this._timerRotacion);
            this._timerRotacion = null;
            this.mensajeFab = "";
            this.objetivoFab = null;
        },

        apuntarMiradaABurbuja() {
            this.$nextTick(() => {
                const burbuja = this.$refs.burbujaFab;
                if (!burbuja) return;
                const caja = burbuja.getBoundingClientRect();
                this.objetivoFab = {
                    x: caja.left + caja.width / 2,
                    y: caja.top + caja.height / 2,
                };
            });
        },

        mensajesParaVista() {
            const nombre = (this.$route && this.$route.name) || "";
            const prefijo = Object.keys(MENSAJES_FAB)
                .find((clave) => nombre.startsWith(clave));
            return prefijo ? MENSAJES_FAB[prefijo] : MENSAJES_FAB_DEFECTO;
        },

        guardarEntrenamiento() {
            if (this.$refs.panel) this.$refs.panel.guardarParaEntrenamiento();
        },

        borrarConversacion() {
            if (this.$refs.panel) this.$refs.panel.limpiarConversacion();
        },

        // ── Pregunta llegada de fuera (?asistente= o el Inicio) ──────────
        abrirConPregunta(pregunta) {
            // El panel es un import async: se espera a que esté montado y la
            // sesión restaurada antes de enviar
            this.abrirPanel();
            this.esperarYEnviar(pregunta, 25);
        },

        esperarYEnviar(pregunta, intentos) {
            if (this._destruido) return; // instancia transitoria: dejar la pregunta a la definitiva
            const panel = this.$refs.panel;
            if (panel && typeof panel.enviarTexto === "function") {
                // Consumir SOLO al entregar: así el redirect de login no la traga
                this.$store.state.asistenteIA.preguntaPendiente = null;
                panel.enviarTexto(pregunta);
                return;
            }
            if (intentos > 0) {
                setTimeout(() => this.esperarYEnviar(pregunta, intentos - 1), 200);
            }
        },
    },
};
</script>

<style scoped>
/* --alto-barra-cambios: lo que mide una barra fija de la página, si la hay (ver EditarPerfil.vue);
   el botón y la burbuja suben para no taparla */
#btn-asistente-ia {
    position: fixed;
    bottom: calc(60px + var(--alto-barra-cambios, 0px));
    right: 45px;
    z-index: 99;
    display: block;
    background-color: #ffffff;
    cursor: pointer;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    padding: 8px;
    box-shadow: 0 8px 28px rgba(35, 57, 118, 0.25);
    /* Al volver a su lugar espera a que la flecha de volver arriba se oculte (0.2s) */
    transition: box-shadow 0.25s ease, bottom 0.25s ease 0.2s;
}

#btn-asistente-ia:hover {
    box-shadow: 0 12px 34px rgba(35, 57, 118, 0.35);
}

#btn-asistente-ia:focus-visible {
    outline: 2px solid #3a55a3;
    outline-offset: 3px;
}

/* Con la flecha de volver arriba visible (FlechaSubir.vue), el botón sube para
   dejarla debajo: 44px de la flecha + 12px de separación. Sube sin esperar: la
   flecha aparece cuando el botón ya terminó, así nunca se enciman. */
#btn-asistente-ia.con-boton-arriba {
    bottom: calc(116px + var(--alto-barra-cambios, 0px));
    transition: box-shadow 0.25s ease, bottom 0.25s ease;
}

/* Burbuja de sugerencias junto al FAB (el gato mira hacia ella) */
.burbuja-fab {
    position: fixed;
    right: 121px;
    bottom: calc(62px + var(--alto-barra-cambios, 0px));
    z-index: 99;
    max-width: 250px;
    background-color: #ffffff;
    color: #233976;
    border: 1px solid #e4e8f3;
    border-radius: 14px;
    border-bottom-right-radius: 4px;
    padding: 10px 14px;
    font-size: 0.85rem;
    line-height: 1.4;
    box-shadow: 0 8px 24px rgba(35, 57, 118, 0.18);
    /* No debe robar el hover del FAB ni bloquear clics de la página */
    pointer-events: none;
    transition: bottom 0.25s ease 0.2s;
}

.burbuja-fab.con-boton-arriba {
    bottom: calc(118px + var(--alto-barra-cambios, 0px));
    transition: bottom 0.25s ease;
}

.burbuja-fab::after {
    content: "";
    position: absolute;
    right: -6px;
    bottom: 16px;
    width: 12px;
    height: 12px;
    background-color: #ffffff;
    border-top: 1px solid #e4e8f3;
    border-right: 1px solid #e4e8f3;
    transform: rotate(45deg);
}

.burbuja-aparece-enter-active,
.burbuja-aparece-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.burbuja-aparece-enter,
.burbuja-aparece-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

/* Panel flotante: ocupa la esquina del botón, por encima del footer fijo y
   por debajo de los modales de Bootstrap (1055) */
.panel-asistente {
    position: fixed;
    right: 45px;
    bottom: 60px;
    z-index: 1040;
    display: flex;
    flex-direction: column;
    width: 400px;
    max-width: calc(100vw - 32px);
    height: 75vh;
    max-height: calc(100vh - 170px);
    background-color: #ffffff;
    border: 1px solid #e4e8f3;
    border-radius: 18px;
    box-shadow: 0 24px 60px rgba(16, 28, 61, 0.28);
    transform-origin: bottom right;
}

.panel-asistente:focus {
    outline: none;
}

@media (max-width: 575.98px) {
    .panel-asistente {
        right: 16px;
    }
}

.panel-aparece-enter-active,
.panel-aparece-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.panel-aparece-enter,
.panel-aparece-leave-to {
    opacity: 0;
    transform: translateY(12px) scale(0.97);
}

/* Sin overflow en el panel: el menú de opciones no debe recortarse */
.cuerpo-asistente {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    border-bottom-left-radius: 17px;
    border-bottom-right-radius: 17px;
}

.encabezado-asistente {
    background: linear-gradient(120deg, #16244d 0%, #233976 45%, #3a55a3 100%);
    padding: 14px 12px 14px 16px;
    border-top-left-radius: 17px;
    border-top-right-radius: 17px;
}

.avatar-asistente {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background-color: white;
    padding: 6px;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

/* Anillo de actividad alrededor del avatar */
.avatar-asistente::after {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 2px solid transparent;
    pointer-events: none;
}

.avatar-asistente.estado-pensando::after {
    border-color: rgba(255, 255, 255, 0.55);
    animation: anillo-actividad 1.1s ease-out infinite;
}

.avatar-asistente.estado-narrando::after {
    border-color: rgba(255, 255, 255, 0.75);
    animation: anillo-actividad 0.7s ease-out infinite;
}

@keyframes anillo-actividad {
    from {
        transform: scale(1);
        opacity: 1;
    }

    to {
        transform: scale(1.28);
        opacity: 0;
    }
}

.titulo-asistente {
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: 0.2px;
}

.estado-enlinea {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    background-color: rgba(255, 255, 255, 0.14);
    padding: 2px 9px;
    border-radius: 20px;
}

.punto-enlinea {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #3ddc84;
    box-shadow: 0 0 5px #3ddc84;
    transition: background-color 0.2s ease;
}

.punto-enlinea.pensando {
    background-color: #ffb74d;
    box-shadow: 0 0 5px #ffb74d;
    animation: pulso-punto 0.9s ease-in-out infinite;
}

.punto-enlinea.narrando {
    background-color: #8fd3ff;
    box-shadow: 0 0 5px #8fd3ff;
    animation: pulso-punto 0.5s ease-in-out infinite;
}

@keyframes pulso-punto {
    50% {
        opacity: 0.35;
    }
}

.subtitulo {
    font-size: 0.74rem;
    opacity: 0.85;
    line-height: 1.3;
}

.min-w-0 {
    min-width: 0;
}

.btn-encabezado {
    color: white;
    background: transparent;
    border: none;
    font-size: 0.95rem;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.btn-encabezado:hover {
    background-color: rgba(255, 255, 255, 0.16);
    color: white;
}

@media (prefers-reduced-motion: reduce) {

    #btn-asistente-ia,
    #btn-asistente-ia.con-boton-arriba,
    .burbuja-fab,
    .burbuja-fab.con-boton-arriba,
    .burbuja-aparece-enter-active,
    .burbuja-aparece-leave-active,
    .panel-aparece-enter-active,
    .panel-aparece-leave-active {
        transition: none;
    }

    .avatar-asistente.estado-pensando::after,
    .avatar-asistente.estado-narrando::after,
    .punto-enlinea.pensando,
    .punto-enlinea.narrando {
        animation: none;
    }
}
</style>
